import {
  Expense,
  ExpenseStatus,
  CreateExpenseInput,
  UpdateExpenseInput,
  ExpenseConnection,
  ApprovalStatus,
} from '../types';
import { User } from '../types/user';
import { DEMO_EXPENSES, DEMO_USERS } from './mockData';

const STORAGE_KEY = 'demo_expenses';
const USERS_STORAGE_KEY = 'demo_users';

/**
 * Mock Expense Service for demo mode
 * Supports full approval workflow: Employee -> Manager -> Finance Admin
 */
export class MockExpenseService {
  private getExpenses(): Expense[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    // Initialize with demo data
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEMO_EXPENSES));
    return DEMO_EXPENSES;
  }

  private saveExpenses(expenses: Expense[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(expenses));
  }

  // Get users from localStorage or use defaults
  private getUsers(): User[] {
    const stored = localStorage.getItem(USERS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    return DEMO_USERS;
  }

  async getMyExpenses(userId?: string): Promise<ExpenseConnection> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const userExpenses = userId
      ? expenses.filter((e) => e.submitterId === userId)
      : expenses;

    return {
      items: userExpenses,
      nextToken: undefined,
      total: userExpenses.length,
    };
  }

  /**
   * Get expenses pending approval for a manager
   * Returns ALL expenses from employees who report to this manager
   * (not just SUBMITTED - so manager can see full history)
   */
  async getTeamExpenses(managerId: string): Promise<ExpenseConnection> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const users = this.getUsers();

    // Find the manager
    const manager = users.find((u) => u.id === managerId);
    if (!manager) {
      console.log('[Demo] Manager not found:', managerId);
      return { items: [], nextToken: undefined, total: 0 };
    }

    // Find all employees who report to this manager
    const teamMembers = users.filter(
      (u) => u.managerEmail === manager.email
    );
    const teamMemberIds = teamMembers.map((u) => u.id);
    
    console.log('[Demo] Manager:', manager.name, 'Team members:', teamMembers.map(t => t.name));

    // Get ALL expenses from team members (so manager can see full history)
    const teamExpenses = expenses.filter(
      (e) => teamMemberIds.includes(e.submitterId)
    );
    
    // Count pending approvals for logging
    const pendingCount = teamExpenses.filter(e => e.status === ExpenseStatus.SUBMITTED).length;
    console.log('[Demo] Total team expenses:', teamExpenses.length, 'Pending approval:', pendingCount);

    return {
      items: teamExpenses,
      nextToken: undefined,
      total: teamExpenses.length,
    };
  }

  /**
   * Get all expenses for finance admin
   * Finance admin sees MANAGER_APPROVED expenses for final approval
   */
  async getAllExpenses(): Promise<ExpenseConnection> {
    await this.delay(300);
    const expenses = this.getExpenses();
    return {
      items: expenses,
      nextToken: undefined,
      total: expenses.length,
    };
  }

  /**
   * Get expenses pending finance approval
   */
  async getPendingFinanceApproval(): Promise<ExpenseConnection> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const pendingExpenses = expenses.filter(
      (e) => e.status === ExpenseStatus.MANAGER_APPROVED
    );

    return {
      items: pendingExpenses,
      nextToken: undefined,
      total: pendingExpenses.length,
    };
  }

  async getExpenseById(id: string): Promise<Expense | null> {
    await this.delay(200);
    const expenses = this.getExpenses();
    return expenses.find((e) => e.id === id) || null;
  }

  async createDraft(input: CreateExpenseInput, userId: string): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();

    const newExpense: Expense = {
      id: `exp-${Date.now()}`,
      title: input.title,
      category: input.category,
      amount: input.amount,
      date: input.date,
      description: input.description || '',
      paymentMethod: input.paymentMethod,
      status: ExpenseStatus.DRAFT,
      submitterId: userId,
      receipts: [],
      approvals: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    expenses.push(newExpense);
    this.saveExpenses(expenses);

    return newExpense;
  }

  async updateDraft(id: string, input: UpdateExpenseInput): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    const expense = expenses[index];
    if (expense.status !== ExpenseStatus.DRAFT) {
      throw new Error('Can only update draft expenses');
    }

    const updated: Expense = {
      ...expense,
      ...input,
      updatedAt: new Date().toISOString(),
    };

    expenses[index] = updated;
    this.saveExpenses(expenses);

    return updated;
  }

  /**
   * Submit expense for approval
   * Status changes: DRAFT -> SUBMITTED
   */
  async submitForApproval(id: string): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    expenses[index] = {
      ...expenses[index],
      status: ExpenseStatus.SUBMITTED,
      updatedAt: new Date().toISOString(),
    };

    this.saveExpenses(expenses);
    console.log(`[Demo] Expense ${id} submitted for manager approval`);
    return expenses[index];
  }

  async deleteDraft(id: string): Promise<void> {
    await this.delay(200);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    if (expenses[index].status !== ExpenseStatus.DRAFT) {
      throw new Error('Can only delete draft expenses');
    }

    expenses.splice(index, 1);
    this.saveExpenses(expenses);
  }

  /**
   * Manager approves expense
   * Status changes: SUBMITTED -> MANAGER_APPROVED
   */
  async managerApprove(
    id: string,
    approverId: string,
    comments?: string
  ): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    const expense = expenses[index];

    if (expense.status !== ExpenseStatus.SUBMITTED) {
      throw new Error('Expense is not pending manager approval');
    }

    const newApproval = {
      id: `apr-${Date.now()}`,
      expenseId: id,
      approverId,
      status: ApprovalStatus.APPROVED,
      comments: comments || 'Approved by manager',
      level: 1,
      timestamp: new Date().toISOString(),
    };

    expenses[index] = {
      ...expense,
      status: ExpenseStatus.MANAGER_APPROVED,
      approvals: [...expense.approvals, newApproval],
      updatedAt: new Date().toISOString(),
    };

    this.saveExpenses(expenses);
    console.log(`[Demo] Expense ${id} approved by manager, pending finance approval`);
    return expenses[index];
  }

  /**
   * Manager rejects expense
   * Status changes: SUBMITTED -> MANAGER_REJECTED
   */
  async managerReject(
    id: string,
    approverId: string,
    comments: string
  ): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    const expense = expenses[index];

    if (expense.status !== ExpenseStatus.SUBMITTED) {
      throw new Error('Expense is not pending manager approval');
    }

    const newApproval = {
      id: `apr-${Date.now()}`,
      expenseId: id,
      approverId,
      status: ApprovalStatus.REJECTED,
      comments,
      level: 1,
      timestamp: new Date().toISOString(),
    };

    expenses[index] = {
      ...expense,
      status: ExpenseStatus.MANAGER_REJECTED,
      approvals: [...expense.approvals, newApproval],
      updatedAt: new Date().toISOString(),
    };

    this.saveExpenses(expenses);
    console.log(`[Demo] Expense ${id} rejected by manager`);
    return expenses[index];
  }

  /**
   * Finance admin approves expense
   * Status changes: MANAGER_APPROVED -> FINANCE_APPROVED
   */
  async financeApprove(
    id: string,
    approverId: string,
    comments?: string
  ): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    const expense = expenses[index];

    if (expense.status !== ExpenseStatus.MANAGER_APPROVED) {
      throw new Error('Expense is not pending finance approval');
    }

    const newApproval = {
      id: `apr-${Date.now()}`,
      expenseId: id,
      approverId,
      status: ApprovalStatus.APPROVED,
      comments: comments || 'Approved by finance',
      level: 2,
      timestamp: new Date().toISOString(),
    };

    expenses[index] = {
      ...expense,
      status: ExpenseStatus.FINANCE_APPROVED,
      approvals: [...expense.approvals, newApproval],
      updatedAt: new Date().toISOString(),
    };

    this.saveExpenses(expenses);
    console.log(`[Demo] Expense ${id} approved by finance, ready for reimbursement`);
    return expenses[index];
  }

  /**
   * Finance admin rejects expense
   * Status changes: MANAGER_APPROVED -> FINANCE_REJECTED
   */
  async financeReject(
    id: string,
    approverId: string,
    comments: string
  ): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    const expense = expenses[index];

    if (expense.status !== ExpenseStatus.MANAGER_APPROVED) {
      throw new Error('Expense is not pending finance approval');
    }

    const newApproval = {
      id: `apr-${Date.now()}`,
      expenseId: id,
      approverId,
      status: ApprovalStatus.REJECTED,
      comments,
      level: 2,
      timestamp: new Date().toISOString(),
    };

    expenses[index] = {
      ...expense,
      status: ExpenseStatus.FINANCE_REJECTED,
      approvals: [...expense.approvals, newApproval],
      updatedAt: new Date().toISOString(),
    };

    this.saveExpenses(expenses);
    console.log(`[Demo] Expense ${id} rejected by finance`);
    return expenses[index];
  }

  /**
   * Mark expense as reimbursed
   * Status changes: FINANCE_APPROVED -> REIMBURSED
   */
  async markReimbursed(id: string): Promise<Expense> {
    await this.delay(300);
    const expenses = this.getExpenses();
    const index = expenses.findIndex((e) => e.id === id);

    if (index === -1) {
      throw new Error('Expense not found');
    }

    const expense = expenses[index];

    if (expense.status !== ExpenseStatus.FINANCE_APPROVED) {
      throw new Error('Expense is not approved for reimbursement');
    }

    expenses[index] = {
      ...expense,
      status: ExpenseStatus.REIMBURSED,
      updatedAt: new Date().toISOString(),
    };

    this.saveExpenses(expenses);
    console.log(`[Demo] Expense ${id} marked as reimbursed`);
    return expenses[index];
  }

  // Legacy method for backward compatibility
  async approveExpense(
    id: string,
    approverId: string,
    comments?: string
  ): Promise<Expense> {
    const expense = await this.getExpenseById(id);
    if (!expense) throw new Error('Expense not found');

    if (expense.status === ExpenseStatus.SUBMITTED) {
      return this.managerApprove(id, approverId, comments);
    } else if (expense.status === ExpenseStatus.MANAGER_APPROVED) {
      return this.financeApprove(id, approverId, comments);
    }
    throw new Error('Expense cannot be approved in current status');
  }

  async rejectExpense(
    id: string,
    approverId: string,
    comments: string
  ): Promise<Expense> {
    const expense = await this.getExpenseById(id);
    if (!expense) throw new Error('Expense not found');

    if (expense.status === ExpenseStatus.SUBMITTED) {
      return this.managerReject(id, approverId, comments);
    } else if (expense.status === ExpenseStatus.MANAGER_APPROVED) {
      return this.financeReject(id, approverId, comments);
    }
    throw new Error('Expense cannot be rejected in current status');
  }

  /**
   * Reset demo data to initial state
   */
  resetDemoData(): void {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[Demo] Data reset to initial state');
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const mockExpenseService = new MockExpenseService();
